import { Button, Icon } from '@chakra-ui/react'
import { atom, useAtom } from 'jotai'
import { X } from 'lucide-react'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Text } from '~/components/core'
import { BannerIconButton } from '~/pages/Home/Banners/BannerControls'
import { BannerProps } from '~/pages/Home/Banners/BannerManager'
import { useElectronUpdaterContext } from '~/providers/ElectronUpdaterContext'
import { isElectron } from '~/utils/common'
import { openInNewTab } from '~/utils/functions'

interface WrapperProps {
  $show: boolean
}

const Wrapper = styled.div<WrapperProps>`
  height: ${({ $show }) => ($show ? '56' : '0')}px;
  padding: 0 16px;
  display: flex;
  width: 100%;
  justify-content: space-between;
  background-color: var(--chakra-colors-orange-solid);
  align-items: center;
  overflow: hidden;
  transition: height 200ms ease;
`

const TextWrapper = styled.div`
  display: flex;
  align-items: center;
`

// In-memory only, so the warning returns on every launch until permissions are fixed.
export const updatePermissionBannerDismissedAtom = atom(false)

const UpdatePermissionBanner = ({ onShowCallback }: BannerProps) => {
  const { t } = useTranslation()
  const { updatePermissionHelpUrl } = useElectronUpdaterContext()
  const [isDismissed, setIsDismissed] = useAtom(
    updatePermissionBannerDismissedAtom,
  )

  const show = isElectron && Boolean(updatePermissionHelpUrl) && !isDismissed

  useEffect(() => {
    onShowCallback(show)
  }, [show, onShowCallback])

  const handleOpenHelp = () => {
    if (updatePermissionHelpUrl) {
      openInNewTab(updatePermissionHelpUrl)
    }
  }

  return (
    <Wrapper $show={show}>
      <TextWrapper>
        <Text color="var(--chakra-colors-orange-contrast)">
          {t(
            'updatePermissionBanner.message',
            "The app can't install updates on this computer because of how it was installed. Follow our help article, or share it with your IT team.",
          )}
        </Text>
        <Button
          marginLeft=".5rem"
          onClick={handleOpenHelp}
          bg="bg.panel"
          color="fg.default"
          borderRadius="full"
          _hover={{ bg: 'bg.muted' }}
          size="sm"
        >
          {t('updatePermissionBanner.howToFix', 'How to fix')}
        </Button>
      </TextWrapper>
      <BannerIconButton
        aria-label={t('tooltips.dismiss', 'Dismiss')}
        marginLeft="0.5rem"
        onClick={() => setIsDismissed(true)}
      >
        <Icon boxSize="20px" color="orange.contrast" aria-hidden>
          <X size="100%" />
        </Icon>
      </BannerIconButton>
    </Wrapper>
  )
}

export default UpdatePermissionBanner
